import { createRoot, hydrateRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import CatalogueQueryProvider from "./lib/CatalogueQueryProvider";
import "./index.css";
import AppProviders from "./AppProviders";
import Root from "./Root";
import { env } from "./config/env";
import { configureAdsOn } from "./lib/ads";
import { configureAnalytics } from "./lib/analytics";
import { ClockContext } from "./lib/clock";
import { preloadPostingDetail, preloadResumeCheck } from "./lib/pageLoaders";
import { appQueryClient } from "./lib/queryClient";
import { isToolPath } from "./lib/routes";
import { readSsrData, seedPosting } from "./lib/ssrData";

// The theme, the query client and the router. Behind them, `Root` puts
// the sign-in over every page and asks for it on the app's pages alone.
// A data router, with one route that hands every address to `Root`'s own
// routes: it is what lets a page with unsaved work ask before it is left.

// Analytics knows where to go, and starts once the person says yes, or,
// where nothing asks first, unless they said no (components/consent).
configureAnalytics({ key: env.POSTHOG_KEY, host: env.POSTHOG_HOST, surface: "app" });
// The ad partners count sign-ups and purchases only while
// analytics runs, and only where the place allows it (lib/ads). Not on a
// tool page, where a resume is pasted: a page that starts here never loads
// them, and every link into the tools loads a new page.
configureAdsOn(window.location.pathname, {
    google: env.GOOGLE_ADS,
    openai: env.OPENAI_ADS_PIXEL,
    meta: env.META_ADS_PIXEL,
});

const container = document.getElementById("root");

if (!container) {
    throw new Error("Root container not found");
}

const queryClient = appQueryClient();
const router = createBrowserRouter([{ path: "*", element: <Root /> }]);
// A server's rendered posting, on the two pages that have it.
const ssrData = readSsrData(document);

const app = (
    <AppProviders
        queryProvider={(children) => (
            <CatalogueQueryProvider client={queryClient}>{children}</CatalogueQueryProvider>
        )}
    >
        <ClockContext.Provider value={ssrData?.renderedAt ?? null}>
            <RouterProvider router={router} />
        </ClockContext.Provider>
    </AppProviders>
);

if (ssrData) {
    seedPosting(queryClient, ssrData);
    // The page's code first. If it will not load, the page's own boundary takes that up.
    const hydrate = () => hydrateRoot(container, app);
    const preload = isToolPath(window.location.pathname)
        ? preloadResumeCheck
        : preloadPostingDetail;
    void preload().then(hydrate, hydrate);
} else {
    createRoot(container).render(app);
}
